import { NavLink } from "react-router-dom";
import { useUser } from "../hooks/useUser";

const TILE_SECTIONS = [
  {
    title: "Alerts",
    roles: ["user", "moderator", "admin", "viewer"],
    tiles: [
      { title: "Alerts", description: "Live volume + price spike alerts for the day", icon: "🔔", path: "/alertspage" },
      { title: "Short List", description: "Your bookmarked tickers and quick notes", icon: "⭐", path: "/shortList" },
      { title: "Charts", description: "TradingView charts on alert tickers", icon: "📈", path: "/charts" },
      { title: "Alert Config", description: "Tune alert thresholds and preferences", icon: "⚙️", path: "/configAlerts" },
    ],
  },
  {
    title: "Trade Journal",
    roles: ["user", "moderator", "admin", "viewer"],
    tiles: [
      { title: "All Trades", description: "Log, review, and analyze every trade", icon: "📓", path: "/trade-journal/all-trades" },
      { title: "Add Trade", description: "Open a new position entry", icon: "➕", path: "/trade-journal/add-trade" },
    ],
  },
  {
    title: "Account",
    roles: ["user", "moderator", "admin", "viewer"],
    tiles: [
      { title: "Profile", description: "Account info, password, preferences", icon: "👤", path: "/profile" },
    ],
  },
  {
    // Read-only bot-operations surface — admins and viewers both see it.
    title: "Trading Bots",
    roles: ["admin", "viewer"],
    tiles: [
      { title: "Observability", description: "All agents at a glance + EOD reconciliation", icon: "📊", path: "/observability" },
      { title: "Bot Trades", description: "Every bot trade with filters, P&L, and live open positions", icon: "💹", path: "/bot-trades" },
      { title: "Agents Reference", description: "All deployed agents: strategy, filters, backtest results", icon: "📖", path: "/agents" },
      { title: "Trading Agent", description: "Auto-trader status, logs, and recent fills", icon: "🤖", path: "/agent" },
    ],
  },
  {
    title: "Admin",
    roles: ["admin"],
    tiles: [
      { title: "All Users", description: "Browse, accept, manage user accounts", icon: "👥", path: "/admin/users" },
      { title: "Registration Requests", description: "Approve or reject pending sign-ups", icon: "✉️", path: "/admin/registrationRequests" },
      { title: "Invitations", description: "Send invitations to new users", icon: "📨", path: "/admin/invitation" },
    ],
  },
];

function Tile({ tile }) {
  return (
    <NavLink to={tile.path} className="home-tile">
      <div className="home-tile-icon">{tile.icon}</div>
      <div className="home-tile-text">
        <div className="home-tile-label">{tile.title}</div>
        <div className="home-tile-desc">{tile.description}</div>
      </div>
    </NavLink>
  );
}

export default function Home() {
  const { user, isAdminAuthenticated, isModeratorAuthenticated, isViewerAuthenticated } = useUser();

  let role = "user";
  if (isAdminAuthenticated) role = "admin";
  else if (isModeratorAuthenticated) role = "moderator";
  else if (isViewerAuthenticated) role = "viewer";

  if (!user) {
    return (
      <div className="home-container">
        <div className="home-header">
          <h1 className="home-title">Welcome to Shoolin</h1>
          <p className="home-subtitle">Please sign in to access your dashboard.</p>
        </div>
      </div>
    );
  }

  const visibleSections = TILE_SECTIONS.filter((s) => s.roles.includes(role));

  return (
    <div className="home-container">
      <div className="home-header">
        <h1 className="home-title">Welcome back, {user.username}</h1>
        <p className="home-subtitle">Pick a feature to get started.</p>
      </div>

      {visibleSections.map((section) => (
        <section key={section.title} className="home-section">
          <h2 className="home-section-title">{section.title}</h2>
          <div className="home-grid">
            {section.tiles.map((tile) => (
              <Tile key={tile.path} tile={tile} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
